import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import LoadingButton from "@mui/lab/LoadingButton";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { TextInput } from "react-admin";
import { runSafelyWithServerClient } from "../api/server.api";
import { useBookedQuotes } from "../hooks/useBookedQuotes";
import { Column } from "../misc/Column";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import type { DialogProps } from "../misc/DialogProps";
import { Form2 } from "../misc/Form2";
import { PdfViewer, type PdfViewerHandle } from "../misc/PdfViewer";
import { SelectInput } from "../inputs/SelectInput";
import type { Student } from "../providers/studentsProvider";

interface GenerateContractPdfFormValues {
  quoteId: string;
  additionalText: string;
}

export function GenerateContractPdfDialog(props: { student: Student } & DialogProps) {
  const { student, open, onClose } = props;
  const [pdf, setPdf] = useState<Blob>();
  const { bookedQuotes, selectedBookedQuote } = useBookedQuotes(student);
  const activeBookedQuotes = useMemo(() => bookedQuotes.filter((quote) => !quote.isCancelled), [bookedQuotes]);
  const initiallySelectedQuoteId =
    activeBookedQuotes.find((quote) => quote.id === selectedBookedQuote.id)?.id ?? activeBookedQuotes[0]?.id;

  const closeDialog = () => {
    setPdf(undefined);
    onClose();
  };

  if (!initiallySelectedQuoteId) {
    return null;
  }

  return (
    <Dialog open={open} onClose={closeDialog} maxWidth={pdf ? "lg" : "sm"} fullWidth>
      <DialogTitle>{pdf ? "Vertragsvorschau" : "Ausbildungsvertrag erstellen"}</DialogTitle>
      <DialogCloseButton onClick={closeDialog} />
      {!pdf ? (
        <_FormDialogContentAndActions
          studentId={student.id}
          bookedQuotes={activeBookedQuotes}
          initiallySelectedQuoteId={initiallySelectedQuoteId}
          setPdf={setPdf}
        />
      ) : (
        <_PreviewDialogContentAndActions pdf={pdf} />
      )}
    </Dialog>
  );
}

function _FormDialogContentAndActions(props: {
  studentId: string;
  bookedQuotes: Array<{ id: string; bundleName: string }>;
  initiallySelectedQuoteId: string;
  setPdf: (pdf: Blob) => void;
}) {
  const { studentId, bookedQuotes, initiallySelectedQuoteId, setPdf } = props;
  const [creatingPreview, setCreatingPreview] = useState(false);

  const createPreview = async (formData: GenerateContractPdfFormValues) => {
    setCreatingPreview(true);
    await runSafelyWithServerClient(
      async (serverClient) => {
        serverClient.use({
          // eslint-disable-next-line @typescript-eslint/require-await
          request: async (_, config) => ({ ...config, responseType: "blob" }),
        });
        const pdf = (await serverClient.generateContractPdf({
          studentId,
          quoteId: formData.quoteId,
          ...(formData.additionalText.trim() ? { additionalText: formData.additionalText } : {}),
        })) as unknown as Blob;
        setPdf(pdf);
      },
      {
        errorMessage: "Fehler beim Erstellen der Vertragsvorschau.",
        successMessage: "",
      },
    );
    setCreatingPreview(false);
  };

  return (
    <Form2
      onSubmit={createPreview}
      defaultValues={{ quoteId: initiallySelectedQuoteId, additionalText: "" } satisfies GenerateContractPdfFormValues}
    >
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Dieser Vertrag wird nur als PDF erstellt und nicht automatisch in AUTOVIO gespeichert.
        </Alert>
        <Column gap={2}>
          {bookedQuotes.length > 1 && (
            <SelectInput
              source="quoteId"
              label="Paket"
              size="small"
              options={bookedQuotes.map((quote) => [quote.id, quote.bundleName])}
            />
          )}
          <TextInput
            source="additionalText"
            label="Zusätzlicher Text (optional)"
            multiline
            rows={6}
            inputProps={{ maxLength: 5000 }}
            helperText="Maximal 5.000 Zeichen"
            fullWidth
          />
        </Column>
      </DialogContent>
      <DialogActions>
        <LoadingButton variant="contained" loading={creatingPreview} type="submit">
          Vorschau erstellen
        </LoadingButton>
      </DialogActions>
    </Form2>
  );
}

function _PreviewDialogContentAndActions({ pdf }: { pdf: Blob }) {
  const pdfViewerRef = useRef<PdfViewerHandle>(null);

  const downloadPdf = () => {
    const url = URL.createObjectURL(pdf);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "Ausbildungsvertrag.pdf";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <DialogContent sx={{ paddingBottom: 0, overflowY: "hidden" }}>
        <PdfViewer ref={pdfViewerRef} pdf={pdf} sx={{ width: "100%", height: "80vh" }} />
      </DialogContent>
      <DialogActions sx={{ paddingTop: "30px" }}>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadPdf}>
          Herunterladen
        </Button>
        <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => pdfViewerRef.current?.print()}>
          Drucken
        </Button>
      </DialogActions>
    </>
  );
}
